import React, { useEffect, useMemo, useState, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import {
  Arrow,
  Communication,
  General,
  HelpCircleFilled,
  Users,
} from 'icons/untitledui';

import routesConfig from 'constants/routesConfig.json';
import colours from 'theme/atoms/colours';
import * as Styled from 'shiftNotes/styles/NoteCaptureResponse.style';
import * as StackStyled from 'shiftNotes/styles/NotesStack.style';

import { Note, InstantReflex } from 'shiftNotes/_types';
import {
  DemoInstantReflexCard,
  DemoTranslateToggle,
} from 'shiftNotes/components/InstantReflex/_index';
import useIsDemoReflex from 'shiftNotes/hooks/useIsDemoReflex';
import useNoteEvent from 'events/hooks/useNoteEvent';
import { deduplicateAndFilterOverlapping } from 'shiftNotes/helpers/nameHighlighting';
import formatNoteDateTime from 'shiftNotes/helpers/formatNoteDateTime';
import getNoteTime from 'shiftNotes/helpers/getNoteTime';
import getNoteAuthorName from 'shiftNotes/helpers/getNoteAuthorName';
import {
  ProfileIcon,
  ProfileIconUser,
  ProfileTag,
} from 'shiftNotes/styles/ProfileTag.style';
import NoteCardGuidanceSection from 'shiftNotes/components/NotesStack/NoteCardGuidanceSection';
import NoteCardLoadingSection from 'shiftNotes/components/NotesStack/NoteCardLoadingSection';
import ConfirmDialog from 'shiftNotes/components/ConfirmDialog';
import { DropdownMenu } from 'shared/components/dropdown/DropdownMenu';
import { usePosthogCapture } from 'shiftNotes/posthogCapture/shiftNotes';
import {
  buildComposeUrl,
  EMAIL_PROVIDERS,
  EmailProvider,
  PROVIDER_LABEL_KEYS,
} from 'shared/components/EmailComposeMenu/providers';

import {
  useDeleteShiftNoteMutation,
  useInstantReflex,
  useGenerateInstantReflexMutation,
  useShiftNote,
} from 'shared/hooks/api/shiftNotes';

export type ResponseSubstate = 'summarized' | 'structured' | 'suggested';

export interface NoteCaptureResponseCardProps {
  note: Note;
  isActive: boolean;
  stackPosition: number;
  isExpanded?: boolean;
  onClose: () => void;
  onClick?: () => void;
}

const getSubstate = (
  note: Note,
  instantReflex: InstantReflex | null | undefined
): ResponseSubstate => {
  const isStructuring =
    note.partial_extracted_at === null && note.entity_extractions.length === 0;

  if (isStructuring) return 'summarized';

  const hasSuggestion =
    !!instantReflex?.text || (instantReflex?.steps?.length ?? 0) > 0;
  if (hasSuggestion) return 'suggested';

  return 'structured';
};

export default function NoteCaptureResponseCard({
  note,
  isActive,
  stackPosition,
  isExpanded = false,
  onClose,
  onClick,
}: NoteCaptureResponseCardProps): JSX.Element {
  const { t } = useTranslation('pageShiftNotes');
  const { t: tShared } = useTranslation('shared');
  const history = useHistory();
  const isDemoReflex = useIsDemoReflex();
  const capture = usePosthogCapture();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [menuMode, setMenuMode] = useState<'main' | 'share'>('main');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    if (!isMenuOpen) setMenuMode('main');
  }, [isMenuOpen]);

  const { data: cachedNote } = useShiftNote(note.id, { enabled: false });
  const currentNote = cachedNote ?? note;
  const { mutate: deleteShiftNote } = useDeleteShiftNoteMutation();

  const dateAtTime = useMemo(
    () => formatNoteDateTime(getNoteTime(currentNote), t),
    [currentNote, t]
  );

  const authorName = getNoteAuthorName(currentNote, t);

  const isExtractionPending =
    currentNote.entity_extractions.length === 0 &&
    !currentNote.detailed_extracted_at;

  const {
    mutate: generateInstantReflex,
    isPending: isGenerating,
    isIdle: isGenerateIdle,
    data: generatedInstantReflex,
  } = useGenerateInstantReflexMutation();

  const shouldPollInstantReflex =
    !!note.instant_reflex?.id &&
    !note.instant_reflex?.text &&
    (note.instant_reflex?.steps?.length ?? 0) === 0;

  const { data: polledInstantReflex } = useInstantReflex(
    note.instant_reflex?.id ?? '',
    shouldPollInstantReflex
  );

  const instantReflex =
    generatedInstantReflex ?? polledInstantReflex ?? note.instant_reflex;

  useEffect(() => {
    if (
      !isDemoReflex &&
      !note.instant_reflex &&
      isGenerateIdle &&
      isActive &&
      !isExtractionPending
    ) {
      generateInstantReflex(note.id);
    }
  }, [
    note.id,
    note.instant_reflex,
    isGenerateIdle,
    isActive,
    isExtractionPending,
    generateInstantReflex,
    isDemoReflex,
  ]);

  const substate = useMemo(
    () => getSubstate(currentNote, instantReflex),
    [currentNote, instantReflex]
  );

  const noteEventId = useNoteEvent(note.id);

  const highlightedText = useMemo(() => {
    const rawText = (
      currentNote.edited_text ||
      currentNote.processed_text ||
      ''
    ).trim();
    const personEntities = currentNote.entity_extractions.filter(
      (e) => e.extraction_class_key.toLowerCase() === 'person'
    );

    if (personEntities.length === 0) return rawText;

    const sorted = deduplicateAndFilterOverlapping(personEntities);
    const parts: React.ReactNode[] = [];
    let cursor = 0;

    sorted.forEach((entity, index) => {
      const start = Math.max(0, entity.start_position);
      const end = Math.max(start, entity.end_position);
      if (end > rawText.length || end <= start) return;

      if (start > cursor) {
        parts.push(rawText.slice(cursor, start));
      }

      const displayName =
        entity.profile?.full_name || rawText.slice(start, end);
      const hasProfile = !!entity.profile;
      parts.push(
        <ProfileTag key={index}>
          {hasProfile ? (
            <ProfileIconUser>
              <Users.User02 size={15} />
            </ProfileIconUser>
          ) : (
            <ProfileIcon>
              <HelpCircleFilled size={15} color={colours.blue[500]} />
            </ProfileIcon>
          )}
          {displayName}
        </ProfileTag>
      );
      cursor = end;
    });

    if (cursor < rawText.length) {
      parts.push(rawText.slice(cursor));
    }

    return parts.length > 0 ? parts : rawText;
  }, [currentNote]);

  const handleClick = (e: MouseEvent) => {
    e.stopPropagation();
    if (onClick) {
      onClick();
      return;
    }
    if (noteEventId) {
      history.push(`${routesConfig.EVENT_PAGE}/${noteEventId}`);
    }
  };

  const stopClickPropagation = (event: MouseEvent) => {
    event.stopPropagation();
  };

  const showContent = isActive || isExpanded;
  const isSummarized = substate === 'summarized';

  const loadingPhase: 'structuring' | 'thinking' = isSummarized
    ? 'structuring'
    : 'thinking';
  const showLoading =
    !isDemoReflex &&
    (isSummarized || (isGenerating && !instantReflex && !isExtractionPending));

  const rawText = (
    currentNote.edited_text ||
    currentNote.processed_text ||
    ''
  ).trim();

  const handleCopy = () => {
    if (typeof navigator === 'undefined' || !navigator.clipboard) return;
    navigator.clipboard.writeText(rawText);
    capture.noteCopied();
  };

  const handleShareToProvider = (provider: EmailProvider) => {
    const subject = currentNote.email_subject ?? '';
    const emailBody = currentNote.email_body ?? rawText;
    window.open(
      buildComposeUrl(provider, { subject, body: emailBody }),
      '_blank',
      'noopener,noreferrer'
    );
    capture.noteShared({ provider });
  };

  const handleDeleteConfirmed = () => {
    if (!currentNote.id) return;
    deleteShiftNote(currentNote.id);
    capture.noteDeleted();
    setShowDeleteConfirm(false);
    onClose();
  };

  const mainMenuItems = [
    {
      label: t('noteCardMenu.share'),
      icon: <Communication.Send01 size={20} />,
      onSelect: () => setMenuMode('share'),
    },
    {
      label: t('noteCardMenu.copy'),
      icon: <General.Copy03 size={20} />,
      onSelect: ({ close }: { close: () => void }) => {
        handleCopy();
        close();
      },
    },
    {
      label: t('noteCardMenu.delete'),
      icon: <General.Trash01 size={20} />,
      destructive: true,
      onSelect: ({ close }: { close: () => void }) => {
        close();
        setShowDeleteConfirm(true);
      },
    },
  ];

  const shareMenuItems = EMAIL_PROVIDERS.map((provider) => ({
    label: tShared(PROVIDER_LABEL_KEYS[provider]),
    icon: <Arrow.ArrowUpRight size={20} />,
    onSelect: ({ close }: { close: () => void }) => {
      handleShareToProvider(provider);
      close();
    },
  }));

  const menuItems = menuMode === 'share' ? shareMenuItems : mainMenuItems;
  const menuHeader =
    menuMode === 'share' ? t('noteCardMenu.shareVia') : undefined;

  return (
    <Styled.Card
      $stackPosition={stackPosition}
      $isActive={isActive}
      $isExpanded={isExpanded}
      onClick={handleClick}
    >
      {showContent && (
        <Styled.ScrollableContent>
          <StackStyled.Card as="div">
            <StackStyled.Header>
              <StackStyled.HeaderAuthor>
                <StackStyled.AuthorName>{authorName}</StackStyled.AuthorName>
                <StackStyled.TimeLabel>{dateAtTime}</StackStyled.TimeLabel>
              </StackStyled.HeaderAuthor>
              {substate === 'suggested' && (
                <StackStyled.MenuWrapper
                  onClick={stopClickPropagation}
                  onKeyDown={stopClickPropagation}
                >
                  <DropdownMenu
                    isOpen={isMenuOpen}
                    onOpenChange={setIsMenuOpen}
                    items={menuItems}
                    headerLabel={menuHeader}
                    usePortal
                    renderTrigger={({ toggle, isOpen }) => (
                      <StackStyled.MenuButton
                        type="button"
                        onClick={(e: MouseEvent<HTMLButtonElement>) => {
                          e.stopPropagation();
                          toggle(e);
                        }}
                        aria-haspopup="true"
                        aria-expanded={isOpen}
                        aria-label={t('noteCardMenu.openMenu')}
                      >
                        <General.DotsHorizontal />
                      </StackStyled.MenuButton>
                    )}
                  />
                </StackStyled.MenuWrapper>
              )}
            </StackStyled.Header>
            <StackStyled.Body as="div">{highlightedText}</StackStyled.Body>
            {isDemoReflex && (
              <div onClick={stopClickPropagation} role="none">
                <DemoTranslateToggle />
              </div>
            )}
            {showLoading && <NoteCardLoadingSection phase={loadingPhase} />}
            {isDemoReflex && !isExtractionPending && (
              <div onClick={stopClickPropagation} role="none">
                <DemoInstantReflexCard noteId={note.id} />
              </div>
            )}
            {!isDemoReflex && substate === 'suggested' && instantReflex && (
              <NoteCardGuidanceSection
                instantReflex={instantReflex}
                noteId={note.id}
                onBackgroundClick={handleClick}
              />
            )}
          </StackStyled.Card>
        </Styled.ScrollableContent>
      )}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title={t('noteCardMenu.deleteTitle')}
        contentText={t('noteCardMenu.deleteMessage')}
        cancelLabel={t('noteCardMenu.deleteCancel')}
        successLabel={t('noteCardMenu.deleteConfirm')}
        handleClose={() => setShowDeleteConfirm(false)}
        successCallback={handleDeleteConfirmed}
      />
    </Styled.Card>
  );
}
